import './MatchDetailScoreboard.less';

import React, { FC } from 'react';
import { Map } from 'immutable';
import { generatePath, Link, useHistory } from 'react-router-dom';
import { useReframe } from 'nike-re-framejs';
import clsx from 'clsx';
import {
    matchGame$$,
    matchOpponets$$,
    matchPeriod$$,
    matchServe$$,
    matchTotalScore$$,
    tournament$$
} from '../../push/overview/subs';
import { isHalfTime, localizeMap } from '../../utils';
import { SvgIcon } from '../../../../icons/SvgIcon';
import { isMobile } from 'react-device-detect';
import { TURBO_LIVE_SPORT_PATH } from '../../../../common/routing';
import { MatchTimer } from '../MatchTimer';
import { getScoreboardType, isNoAdvantageExtra } from '../../matchExtras';
import { MatchNotification } from './MatchNotification';
import { detailMiniScoreVisible$, detailMiniTimerVisible$ } from '../../subs';
import { ErrorBoundary } from '../../../../common/ErrorBoundary';
import i18n from '../../../../common/i18n';
import { StatsIcon } from '../MatchIcons';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { createPortal } from 'react-dom';
import { isMobileChannel } from '../../../../common/util';
import { getSportIdByName } from '../../../../common/utils/utils';

const MatchDetailScore = ({ scores, hybrid = false }) => {
    if (!scores || scores.size === 0) {
        return null;
    }
    const mainScore = scores.get('GAME') || scores.get('TOTAL');
    return (
        <span
            className={clsx('text-extra-bold mx-5', {
                'c-orange': hybrid
            })}
            data-atid="tlv-matchDetail-totalScore"
        >
            {`${mainScore.get('home')} : ${mainScore.get('away')}`}
        </span>
    );
};

const MatchDetailLiveIcon = () => (
    <SvgIcon
        imgId="live"
        sizeX="34"
        sizeY="16"
        className={`match-detail-live p-abs c-white flex-shrink-0 ${isMobileChannel() ? 'mr-8' : 'mr-15'}`}
    />
);

export const MatchDetailPeriods = ({ periodScores, sportId, scoreboardTypeId, timer, hybrid = false }) => {
    const matchMedia = useMatchMedia();
    const noHybridClassNames = `${matchMedia.xxxl ? 'py-10' : 'py-3'} flex-center c-white p-rel`;
    const customPeriods = (sportId === 85 || sportId === 88 || sportId === 59) && periodScores?.size > 1;
    return periodScores?.size || timer ? (
        <div
            className={clsx('fs-12', {
                [noHybridClassNames]: !hybrid,
                [`c-black-50 ${customPeriods ? 'pl-10 flex' : 'pl-5'}`]: hybrid
            })}
        >
            {!hybrid && (
                <MatchTimer scoreboardTypeId={scoreboardTypeId} matchTimer={timer} otherClass="fs-12 c-white mr-10" />
            )}
            {customPeriods ? (
                <>
                    <div>
                        {i18n(
                            'betradar.snooker.lastPeriod',
                            periodScores.getIn([periodScores.size - 2, 'score', 'home']),
                            periodScores.getIn([periodScores.size - 2, 'score', 'away'])
                        )}
                    </div>
                    <div className="pl-10 text-extra-bold">
                        {i18n(
                            'betradar.snooker.scoreNow',
                            periodScores.getIn([periodScores.size - 1, 'score', 'home']),
                            periodScores.getIn([periodScores.size - 1, 'score', 'away'])
                        )}
                    </div>
                </>
            ) : periodScores?.size ? (
                <>
                    {'('}
                    {periodScores.map((period, i) => (
                        <span
                            key={i}
                            className={clsx('px-3', {
                                'text-extra-bold': periodScores.size - 1 === i,
                                'br-radius-xxxs mx-3': isHalfTime(period.get('periodType')),
                                'match-detail-br': isHalfTime(period.get('periodType')) && !hybrid,
                                'br-default': isHalfTime(period.get('periodType')) && hybrid
                            })}
                            data-atid={'tlv-matchDetail-periodScore'}
                        >
                            {`${period.getIn(['score', 'home'])}:${period.getIn(['score', 'away'])}`}
                            {i < periodScores.size - 1 && ','}
                        </span>
                    ))}
                    {')'}
                </>
            ) : null}
            {!hybrid && <MatchDetailLiveIcon />}
        </div>
    ) : null;
};

const MatchDetailOpponentsMini = ({ matchId, sportName }) => {
    const derefSub = useReframe();
    const [home, away] = derefSub(matchOpponets$$(matchId, sportName));
    const totalScore = derefSub(matchTotalScore$$(matchId, sportName));
    if (!totalScore) {
        return null;
    }
    return (
        <div className="flex match-detail-fadeIn">
            <span className={clsx('flex-1 ellipsis', { 'text-right': away })}>{home}</span>
            {away && (
                <>
                    <span className="text-extra-bold px-5">{`${totalScore.get('home')} : ${totalScore.get(
                        'away'
                    )}`}</span>
                    <span className="flex-1 ellipsis text-left">{away}</span>
                </>
            )}
        </div>
    );
};

function getMobileStatsPadding(hybrid, server, stats) {
    return hybrid ? (server ? '15' : stats ? '20' : '0') : '15';
}

export const MatchDetailOpponnets = ({ home, away, scores, server, stats, hybrid = false }) => {
    const matchMedia = useMatchMedia();
    const localizedHome = localizeMap(home);
    const localizedAway = localizeMap(away);
    const cards = stats?.get('RED_CARDS');
    const penalties = stats?.get('SUSPENDS');
    const homeStats = parseInt(penalties?.split(':')[0]) || parseInt(cards?.split(':')[0]);
    const awayStats = parseInt(penalties?.split(':')[1]) || parseInt(cards?.split(':')[1]);
    const statsCss = homeStats || awayStats ? 'flex p-rel items-center' : '';
    let titleExplain = '';
    if (cards) {
        titleExplain = i18n('nui.turbo.live.match.redCards');
    } else if (penalties) {
        titleExplain = i18n('nui.turbo.live.match.penalties');
    }

    return (
        <>
            <div
                className={clsx(
                    hybrid ? 'text-bold flex-1' : 'match-detail-opponents flex-center c-white',
                    `${isMobile || hybrid ? 'fs-14 flex' : 'fs-16'}`,
                    { [`${matchMedia.xxxl ? 'py-10' : 'py-3'}`]: !hybrid, flex: hybrid }
                )}
            >
                <span
                    className={clsx(
                        'ellipsis',
                        hybrid && localizedAway ? 'match-detail-scoreboard-hybrid flex p-rel' : 'flex-1',
                        statsCss,
                        `${
                            localizedAway
                                ? `text-right justify-end ${hybrid ? '' : 'pl-5'}`
                                : !hybrid
                                ? 'text-center'
                                : ''
                        }`,
                        {
                            [`pr-${
                                isMobile || hybrid ? getMobileStatsPadding(hybrid, server, homeStats) : '30'
                            }`]: localizedAway
                        }
                    )}
                >
                    <span className="ellipsis" data-atid="scoreboard-opponent-home">
                        {localizedHome}
                    </span>
                    {homeStats > 0 && (
                        <span
                            className={clsx(homeStats && `match-stats-${hybrid ? 'hybrid-' : ''}r`, {
                                'match-penalties match-stats-r p-abs': penalties && homeStats,
                                'match-cards p-abs flex-center': cards && homeStats,
                                'match-cards-detail fs-12': cards && homeStats && !hybrid,
                                'match-penalties-detail': penalties && homeStats && !hybrid
                            })}
                            title={titleExplain}
                        >
                            {homeStats}
                        </span>
                    )}
                    {server === 'HOME' && (
                        <span
                            className={clsx(`match-serve match-serve-detail-${hybrid ? 'hybrid-' : ''}r`, {
                                'match-detail-serve': !hybrid
                            })}
                            title={i18n('nui.turbo.live.match.server')}
                        />
                    )}
                </span>
                {localizedAway &&
                    (isMobile && !hybrid ? (
                        <span className="turbo-live-opacity">vs</span>
                    ) : (
                        <MatchDetailScore scores={scores} hybrid={hybrid} />
                    ))}
                {localizedAway && (
                    <span
                        className={clsx(
                            'flex-1 ellipsis justify-start pr-8',
                            `pl-${isMobile || hybrid ? getMobileStatsPadding(hybrid, server, awayStats) : '30'}`,
                            statsCss,
                            {}
                        )}
                    >
                        {awayStats > 0 && (
                            <span
                                className={clsx(awayStats && `match-stats-${hybrid ? 'hybrid-' : ''}l`, {
                                    'match-penalties p-abs': penalties && awayStats,
                                    'match-cards p-abs flex-center': cards && awayStats,
                                    'match-cards-detail fs-12': cards && awayStats && !hybrid,
                                    'match-penalties-detail': penalties && homeStats && !hybrid
                                })}
                                title={titleExplain}
                            >
                                {awayStats}
                            </span>
                        )}
                        {server === 'AWAY' && (
                            <span
                                className={clsx(`match-serve match-serve-detail-${hybrid ? 'hybrid-' : ''}l`, {
                                    'match-detail-serve': !hybrid
                                })}
                                title={i18n('nui.turbo.live.match.server')}
                            />
                        )}
                        <span className={'ellipsis'} data-atid="scoreboard-opponent-away">
                            {localizedAway}
                        </span>
                    </span>
                )}
            </div>
        </>
    );
};

export const MatchDetailScoreboardDummy: FC<{ sportId: string | number }> = ({ sportId }) => (
    <div className={`match-detail-scoreboard${isMobile ? '-sm' : ''} flex flex-col bg-${sportId}`} />
);

const MatchDetaiNote: FC<{ note?: string; noAdvantage?: any; tiebreak?: string }> = ({
    note,
    noAdvantage,
    tiebreak
}) => {
    const text = noAdvantage && localizeMap(noAdvantage.get('header'));
    const tooltip = noAdvantage && localizeMap(noAdvantage.get('tooltip'));
    const noteClass = 'c-white turbo-live-opacity';
    return (
        <div
            className={clsx('match-detail-note pr-10 fs-12', {
                'pl-10 text-center': isMobile,
                'text-center': isMobileChannel()
            })}
        >
            {note && <span className={noteClass} title={note}>{`${note}${text || tiebreak ? ', ' : ''}`}</span>}
            {noAdvantage && <span className={noteClass} title={tooltip}>{`${text}${tiebreak ? ', ' : ''}`}</span>}
            {tiebreak && (
                <span className={noteClass} title={tiebreak}>
                    {tiebreak}
                </span>
            )}
        </div>
    );
};

export const BackToOverview = ({ path, classes, children }) => {
    const history = useHistory();
    const fromApp = history?.location?.state?.['fromApp'];
    const title = i18n('nui.turbo.live.back.to.overview');
    if (fromApp) {
        return (
            <span className={clsx('pointer', classes)} onClick={history.goBack} title={title}>
                {children}
            </span>
        );
    } else {
        return (
            <Link to={path} className={classes} title={title}>
                {children}
            </Link>
        );
    }
};

const MatchDetailBackPortal: FC<{
    tournamentName;
    path;
    note?: string;
    noAdvantage?;
    tiebreak?: string;
    hybrid?: boolean;
}> = ({ tournamentName, path, note, noAdvantage, tiebreak, hybrid }) =>
    hybrid ? (
        <div className="flex-center c-white fs-12 ellipsis match-detail-tournament">{tournamentName}</div>
    ) : (
        <BackToOverview path={path} classes="flex items-center c-white fs-12">
            <SvgIcon imgId="live-back" sizeX="16" sizeY="14" className="mr-5 my-15 c-white" />
            <span className="ellipsis c-white px-10" title={tournamentName}>
                {tournamentName}
            </span>
            <MatchDetaiNote note={note} noAdvantage={noAdvantage} tiebreak={tiebreak} />
        </BackToOverview>
    );

const MatchDetailBackMobile: FC<{
    tournamentName;
    path;
    matchId;
    scoreboardTypeId?: string;
    sportName: string;
    matchTimer;
}> = ({ matchId, scoreboardTypeId, sportName, matchTimer, tournamentName, path }) => {
    const derefSub = useReframe();
    const miniScoreVisible = derefSub(detailMiniScoreVisible$);
    const miniTimerVisible = derefSub(detailMiniTimerVisible$);
    return (
        <>
            <BackToOverview path={path} classes={'flex items-center justify-start'}>
                <SvgIcon imgId="live-back" sizeX="16" sizeY="12" className="mr-15 c-white" />
            </BackToOverview>
            <Link to={path} className="flex flex-col flex-3 justify-center ellipsis fs-12 text-center">
                <div className="ellipsis c-white">
                    {miniScoreVisible ? (
                        <MatchDetailOpponentsMini matchId={matchId} sportName={sportName} />
                    ) : (
                        tournamentName
                    )}
                </div>
                {miniTimerVisible && (
                    <div className="flex-center c-white fs-11 match-detail-anim">
                        <MatchTimer
                            scoreboardTypeId={scoreboardTypeId}
                            matchTimer={matchTimer}
                            otherClass="flex-center"
                        />
                        <MatchPeriod matchId={matchId} sportName={sportName} />
                        <MatchServerGame matchId={matchId} sportName={sportName} />
                    </div>
                )}
            </Link>
            <SvgIcon imgId="live" sizeX="30" sizeY="14" className="c-white" />
        </>
    );
};

const MatchServerGame: FC<{ matchId: string; sportName: string }> = ({ matchId, sportName }) => {
    const derefSub = useReframe();
    const game = derefSub(matchGame$$(matchId, sportName));
    const serve = derefSub(matchServe$$(matchId, sportName));
    return game ? (
        <span className="flex-center match-detail-serving">
            <span className={clsx('px-20', { [` match-detail-serve-${serve.toLowerCase()}`]: serve })}>
                {game.get('home')} : {game.get('away')}
            </span>
        </span>
    ) : null;
};

const MatchPeriod: FC<{ matchId: string; sportName: string }> = ({ matchId, sportName }) => {
    const derefSub = useReframe();
    const period = derefSub(matchPeriod$$(matchId, sportName));
    return period ? (
        <span className="pl-8 pr-10 text-extra-bold">
            {period.get('home')} : {period.get('away')}
        </span>
    ) : null;
};

const MatchDetailBack: FC<{
    matchId: string;
    scoreboardTypeId?: string;
    matchTimer;
    sportName;
    note?;
    tournamentName?: string;
    noAdvantage?;
    tiebreak?;
    hybrid?: boolean;
}> = ({ matchId, scoreboardTypeId, matchTimer, sportName, note, tournamentName, noAdvantage, tiebreak, hybrid }) => {
    const path = generatePath(TURBO_LIVE_SPORT_PATH, { sportName });
    const localizeTiebreak = tiebreak && localizeMap(tiebreak);
    return isMobileChannel() ? (
        <MatchDetailBackMobile
            matchId={matchId}
            scoreboardTypeId={scoreboardTypeId}
            sportName={sportName}
            matchTimer={matchTimer}
            path={path}
            tournamentName={tournamentName}
        />
    ) : (
        <MatchDetailBackPortal
            path={path}
            tournamentName={tournamentName}
            note={note}
            noAdvantage={noAdvantage}
            tiebreak={localizeTiebreak}
            hybrid={hybrid}
        />
    );
};

export const MatchDetailIcons: FC<{ statisticsId: number; matchId: string; sportId: string }> = ({
    statisticsId,
    matchId,
    sportId
}) => (
    <>
        <MatchNotification matchId={matchId} sportId={sportId} />
        <StatsIcon
            statisticsId={statisticsId}
            btnClass={`br-radius-circle mx-10 bg-white-ng ${
                isMobileChannel() ? 'my-10 px-10 py-10' : 'my-7 px-8 py-8'
            }`}
            iconClass="c-black-50"
        />
    </>
);

export const MatchDetailScoreboard: FC<{ sportName: string; match: Map<string, any> }> = ({ sportName, match }) => {
    return match ? (
        <ErrorBoundary description="TurboLive MatchDetailScoreboard">
            <MatchDetailScoreboardView sportName={sportName} match={match} />
        </ErrorBoundary>
    ) : (
        <MatchDetailScoreboardDummy sportId={getSportIdByName(sportName)} />
    );
};

const MatchDetailScoreboardView: FC<{ sportName: string; match: Map<string, any> }> = ({ sportName, match }) => {
    const derefSub = useReframe();
    const tournamentId = match.get('tournamentId');
    const tournament = derefSub(tournament$$(sportName, tournamentId));
    const tournamentName = tournament && localizeMap(tournament.get('tournamentName'));
    const container = document.getElementById('LayoutMainSticky');
    const scoreboard = (
        <MatchDetailScoreboardContent
            sportName={sportName}
            match={match}
            tournamentName={tournamentName}
            fixed={!container}
        />
    );
    return container ? createPortal(scoreboard, container) : scoreboard;
};

export const MatchDetailScoreboardContent: FC<{
    sportName;
    tournamentName: string;
    match: Map<string, any>;
    hybrid?;
    fixed?: boolean;
}> = ({ sportName, tournamentName, match, hybrid, fixed }) => {
    const matchId = match.get('id');
    const note = match.get('note');
    const score = match.get('score');
    const noAdvantage = isNoAdvantageExtra(match);
    const sportId = match.get('sportId');
    const isMobileView = isMobileChannel();
    const matchAway = match.get('away');
    const matchTimer = match.get('timer');
    const statisticsId = match.get('statisticsId');
    const timer = match.get('timer');
    const scoreFixed = isMobileView && fixed;

    return (
        <ErrorBoundary description="TurboLive MatchDetailScoreboard">
            <div
                className={clsx(`flex flex-col bg-${sportId}`, {
                    'match-detail-scoreboard': !isMobileView,
                    'match-detail-scoreboard-tablet p-rel': isMobileView && !fixed
                })}
            >
                {scoreFixed && <div className="match-detail-scoreboard-mini" />}
                <div
                    className={clsx('flex', {
                        [`match-detail-scoreboard-mini items-center px-15 bg-${sportId}`]: isMobileView,
                        'p-fix': scoreFixed,
                        'pl-20 pr-10': !isMobileView
                    })}
                >
                    <MatchDetailBack
                        matchId={matchId}
                        scoreboardTypeId={getScoreboardType(match)}
                        matchTimer={matchTimer}
                        sportName={sportName}
                        tournamentName={tournamentName}
                        note={localizeMap(note)}
                        noAdvantage={noAdvantage && match.getIn(['extras', 'noAdvantage'])}
                        tiebreak={match.getIn(['timer', 'tieBreak'])}
                        hybrid={hybrid}
                    />
                    {!isMobileView && (
                        <div className={clsx('flex flex-1 items-center justify-end')}>
                            <MatchDetailIcons matchId={matchId} sportId={sportId} statisticsId={statisticsId} />
                        </div>
                    )}
                </div>
                {isMobileView && (
                    <MatchDetaiNote
                        note={localizeMap(note)}
                        noAdvantage={noAdvantage && match.getIn(['extras', 'noAdvantage'])}
                        tiebreak={localizeMap(match.getIn(['timer', 'tieBreak']))}
                    />
                )}
                {!isMobileView && (
                    <MatchDetailOpponnets
                        home={match.get('home')}
                        away={matchAway}
                        scores={score?.get('scores')}
                        server={score?.get('server')}
                        stats={match.get('stats')}
                    />
                )}
                {!isMobileView && score && timer && (
                    <MatchDetailPeriods
                        scoreboardTypeId={getScoreboardType(match)}
                        periodScores={matchAway && score.get('periodScores')}
                        sportId={parseInt(sportId)}
                        timer={timer}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
};
